import React, { cloneElement } from 'react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import RoomIcon from '@material-ui/icons/Room';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { toFormatDate } from '../../../utils/date';

export const DATA_SOURCE_BACKOFFICE = 'backoffice';

// Local helpers adapted to MUI v4 environment
const CustomTypography: React.FC<{ children?: React.ReactNode; noWrap?: boolean }> = ({ children, noWrap }) => (
    <Typography variant='caption' style={{ fontSize: 12, opacity: 0.54, color: 'black' }} noWrap={noWrap}>
        {children}
    </Typography>
);

const Dot = () => <CustomTypography>·</CustomTypography>;

const renderIconByDataSource = (dataSource: string) =>
    dataSource?.toLowerCase() === DATA_SOURCE_BACKOFFICE.toLowerCase() ? <DesktopWindowsIcon /> : <PhoneAndroidIcon />;

const Item: React.FC<{ icon: React.ReactElement; tooltipText?: string }> = ({ icon, tooltipText }) => {
    const outIcon = cloneElement(icon, {
        style: { fontSize: 15, color: '#757575' },
    } as any);

    const iconNode = tooltipText ? <Tooltip title={tooltipText}>{outIcon}</Tooltip> : outIcon;

    return (
        <Box display='grid' gridTemplateColumns='repeat(2, auto)' alignItems='center'>
            <Dot />
            {iconNode}
        </Box>
    );
};

interface AuthInfo {
    firstName?: string;
    lastName?: string;
    username?: string;
}

interface Coords {
    latitude: number;
    longitude: number;
    addressText?: string;
}

interface Props {
    title: any;
    icon: any;
    time: string;
    authInfo?: AuthInfo;
    coords?: Coords;
    dataSource?: string;
    visible?: boolean;
}

const renderDangerousTextLocal = (value: any) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
        return <span dangerouslySetInnerHTML={{ __html: value }} />;
    }
    return value;
};

const NoteTitle: React.FC<Props> = ({ title, icon, time, authInfo, dataSource, coords, visible }) => {
    const translate = useTranslate();

    return (
        <Box display='grid' gridTemplateColumns='auto 1fr' alignItems='center' gridGap={8} px={1}>
            {cloneElement(icon, {
                color: 'primary',
                style: { fontSize: 22 },
            })}

            <Box display='grid'>
                <Typography style={{ fontSize: 15, fontWeight: 600, color: '#092b4b' }}>
                    {renderDangerousTextLocal(title)}
                </Typography>

                <Box
                    display='grid'
                    gridGap={4}
                    style={{
                        gridTemplateColumns: `auto ${authInfo ? 'auto' : ''} auto` as any,
                        justifyContent: 'start',
                        alignItems: 'center',
                    }}
                >
                    <CustomTypography>{toFormatDate(time)}</CustomTypography>

                    {authInfo && (
                        <Box
                            display='grid'
                            gridTemplateColumns='repeat(3, auto)'
                            alignItems='center'
                            style={{ gridGap: 4 }}
                        >
                            <Dot />
                            <CustomTypography>{translate('app.notes.makeBy')}</CustomTypography>
                            <CustomTypography noWrap>
                                <b>{authInfo?.firstName ?? authInfo?.username}</b>
                            </CustomTypography>
                        </Box>
                    )}

                    <Box display='flex' alignItems='center'>
                        {dataSource && <Item icon={renderIconByDataSource(dataSource) as any} />}
                        {coords && <Item icon={<RoomIcon />} />}
                        {visible !== undefined && visible === false && <Item icon={<RemoveCircleOutlineIcon />} />}
                    </Box>
                </Box>

                {coords?.addressText && <CustomTypography>{coords.addressText}</CustomTypography>}
            </Box>
        </Box>
    );
};

export default NoteTitle;
